/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { events } from '@adobe-commerce/event-bus';
import { SET_NEGOTIABLE_QUOTE_SHIPPING_ADDRESS_MUTATION } from './graphql/SetNegotiableQuoteShippingAddressMutation';
import { NegotiableQuoteModel } from '@/quote-management/data/models/negotiable-quote-model';
import { transformQuote } from '@/quote-management/data/transforms';

export interface AddressInput {
  /** City name */
  city: string;
  /** Optional company name */
  company?: string;
  /** Two-letter country code (e.g., 'US') */
  countryCode: string;
  /** First name */
  firstname: string;
  /** Last name */
  lastname: string;
  /** Postal/ZIP code */
  postcode: string;
  /** Optional state/province name */
  region?: string;
  /** Optional state/province ID */
  regionId?: number;
  /** Whether to save this address to the customer's address book */
  saveInAddressBook?: boolean;
  /** Street address lines (array) */
  street: string[];
  /** Phone number */
  telephone: string;
  /** 
   * Additional input (optional custom fields for the address).
   * These fields will be merged into the address data sent to GraphQL.
   * Standard fields take precedence over additional fields to prevent
   * accidental override of required address attributes.
   * 
   * @example
   * ```ts
   * additionalInput: {
   *   vat_id: 'GB123456789',
   *   custom_attribute: 'value',
   *   delivery_instructions: 'Leave at door'
   * }
   * ```
   */
  additionalInput?: Record<string, any>;
}

export interface SetShippingAddressInput {
  /** The unique ID of the negotiable quote */
  quoteUid: string;
  /** The ID of a saved customer address. Provide exactly one of addressId, companyAddressId or addressData. */
  addressId?: number | string;
  /**
   * The ID of an address from the company address book. Required instead of
   * addressId once the company address book is enabled, because the backend
   * then rejects both a customer address and a new address payload.
   */
  companyAddressId?: number | string;
  /** New address data. Provide exactly one of addressId, companyAddressId or addressData. */
  addressData?: AddressInput;
}

/**
 * Transform camelCase address data to snake_case for GraphQL mutation.
 * Additional fields from additionalInput are spread into the result, with
 * standard fields taking precedence (defensive flexibility approach).
 */
function transformAddressToSnakeCase(address: AddressInput) {
  const { additionalInput, ...standardFields } = address;
  
  // Build the standard transformed fields
  const transformedStandardFields = {
    city: standardFields.city,
    company: standardFields.company,
    country_code: standardFields.countryCode,
    firstname: standardFields.firstname,
    lastname: standardFields.lastname,
    postcode: standardFields.postcode,
    region: standardFields.region,
    region_id: standardFields.regionId,
    save_in_address_book: standardFields.saveInAddressBook,
    street: standardFields.street,
    telephone: standardFields.telephone,
  };
  
  // Spread additional input first, then standard fields take precedence
  // This provides defensive flexibility: allows custom fields while protecting core fields
  return {
    ...(additionalInput || {}),
    ...transformedStandardFields,
  };
}

/**
 * Sets or updates the shipping address for a negotiable quote.
 * 
 * @param input - The input parameters for setting the shipping address
 * @param input.quoteUid - The unique ID of the negotiable quote
 * @param input.addressId - Optional ID of a saved customer address (use this OR addressData)
 * @param input.addressData - Optional new address data (use this OR addressId)
 * @param input.addressData.additionalInput - Optional additional address fields to pass through to GraphQL
 * @returns Promise that resolves to the updated quote model with shipping addresses including uid field
 * @throws Error if validation fails or GraphQL operation fails
 * 
 * @example
 * ```ts
 * // Using a saved address
 * const quote = await setShippingAddress({
 *   quoteUid: 'quote-123',
 *   addressId: 5
 * });
 * 
 * // Using new address data
 * const quote = await setShippingAddress({
 *   quoteUid: 'quote-123',
 *   addressData: {
 *     firstname: 'John',
 *     lastname: 'Doe',
 *     street: ['123 Main St'],
 *     city: 'San Francisco',
 *     countryCode: 'US',
 *     postcode: '94103',
 *     telephone: '555-1234'
 *   }
 * });
 * 
 * // Using address data with additional custom fields
 * const quote = await setShippingAddress({
 *   quoteUid: 'quote-123',
 *   addressData: {
 *     firstname: 'John',
 *     lastname: 'Doe',
 *     street: ['123 Main St'],
 *     city: 'San Francisco',
 *     countryCode: 'US',
 *     postcode: '94103',
 *     telephone: '555-1234',
 *     additionalInput: {
 *       vat_id: 'GB123456789',
 *       custom_attribute_1: 'value1',
 *       delivery_instructions: 'Leave at door'
 *     }
 *   }
 * });
 * 
 * // Access address identifier
 * if (quote?.shippingAddresses?.[0]) {
 *   console.log('Address UID:', quote.shippingAddresses[0].uid);
 * }
 * ```
 */
export const setShippingAddress = async (
  input: SetShippingAddressInput
): Promise<NegotiableQuoteModel | null> => {
  const { quoteUid, addressId, companyAddressId, addressData } = input;

  if (!quoteUid) {
    throw new Error('Quote UID is required');
  }

  // Validate that exactly one address source is provided. `!= null` rather than
  // `!== undefined`: an explicit null is the absence of an id, and treating it
  // as a source would send a null one and leave the caller with a backend error
  // instead of the message below.
  const providedSources = [
    addressId != null,
    companyAddressId != null,
    Boolean(addressData),
  ].filter(Boolean).length;

  if (providedSources === 0) {
    throw new Error(
      'One of addressId, companyAddressId or addressData must be provided'
    );
  }

  if (providedSources > 1) {
    throw new Error(
      'Provide only one of addressId, companyAddressId or addressData'
    );
  }

  // Transform addressData to snake_case if provided
  const transformedAddressData = addressData
    ? transformAddressToSnakeCase(addressData)
    : null;

  return fetchGraphQl(SET_NEGOTIABLE_QUOTE_SHIPPING_ADDRESS_MUTATION, {
    variables: {
      quoteUid,
      // Nullish, not falsy: an id of `0` or `''` passed the check above, so
      // coercing it to null here would send something the caller never asked for.
      addressId: addressId ?? null,
      companyAddressId: companyAddressId ?? null,
      addressData: transformedAddressData,
    },
  }).then((response) => {
    const { errors } = response;

    if (errors) {
      // Provide more detailed error information
      const errorMessages = errors.map((error) => error.message).join('; ');
      throw new Error(`Failed to set shipping address: ${errorMessages}`);
    }

    // Transform the quote data
    const quoteData = transformQuote(
      response.data?.setNegotiableQuoteShippingAddress?.quote
    );

    if (!quoteData) {
      throw new Error(
        'Failed to transform quote data: Invalid response structure'
      );
    }

    // Emit event with transformed quote data on success
    events.emit('quote-management/shipping-address-set', {
      quote: quoteData,
      input: { quoteUid, addressId, companyAddressId, addressData },
    });

    return quoteData;
  });
};

